import React, { useState, useEffect } from 'react';
import FeaturedSVG from '../assets/FeaturedLogo.svg';
import { Stack, Typography, TextField, Box, Modal, Button, Grid, Container, CircularProgress, Alert } from '@mui/material';
import EnquiryImage from "../assets/EnquiryPage/EnquiryPage.jpg";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { useLocation } from 'react-router-dom';

const Enquiry = () => {
  const location = useLocation();
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [contactNo, setContactNo] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (location.state && location.state.requirement) {
      setMessage(location.state.requirement);
    }
  }, [location.state]);

  const [loading, setLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const sendEmail = async () => {
    if (!name || !email || !contactNo || !message) {
      setAlertMessage('Please fill in all fields!');
      setAlertSeverity('warning');
      setAlertOpen(true);
      return;
    }

    setLoading(true);

    try {
      const emailSubject = `Enquiry from ${name}`;
      const emailBody = `
        <p><strong>Name:</strong> ${name}</p>
        <p><strong>Email:</strong> ${email}</p>
        <p><strong>Contact No.:</strong> ${contactNo}</p>
        <p><strong>Company Name.:</strong> ${companyName}</p>
        <p><strong>Message:</strong> ${message}</p>
      `;

      const response = await fetch('https://maxweighwebsiteemailfunctionapp.azurewebsites.net/api/funcemailintegration?code=kunIUQrdWU2jCZQVE4Ikm8sbVPqprIjpXeAyoLFc30LyAzFu4ffGTQ%3D%3D', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emailSubject: emailSubject,
          emailBody: emailBody,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('We have received your information, we will get in contact with you soon', result);
        setAlertMessage('Your message has been sent!');
        setAlertSeverity('success');
        setAlertOpen(true);
        // Reset form
        setName('');
        setCompanyName('');
        setEmail('');
        setContactNo('');
        setMessage('');
      } else {
        console.error('Error sending email:', response.statusText);
        setAlertMessage('Failed to send your details. Please directly contact us on maxweighsystem@gmail.com.');
        setAlertSeverity('error');
        setAlertOpen(true);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setAlertMessage('Failed to send your details. Please directly contact us on maxweighsystem@gmail.com.');
      setAlertSeverity('error');
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ py: 8, backgroundColor: '#fff', position: 'relative' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box component="img" src={FeaturedSVG} alt="Featured SVG" sx={{ width: '100%', maxWidth: '400px', height: 'auto', mb: 2 }} />
          <Typography
            variant="h3"
            sx={{
              color: '#B1B1B1',
              fontFamily: 'Inter, sans-serif',
              fontSize: '24px',
              fontWeight: 400,
              textTransform: 'uppercase',
              letterSpacing: '2px',
              mb: 2
            }}
          >
            ENQUIRY
          </Typography>
          <Typography
            sx={{
              color: '#000',
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: '18px', md: '24px' },
              fontWeight: 400,
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            Have questions about our weighing scales? Submit your enquiry or request a quotation today!
          </Typography>
        </Box>

        <Grid container spacing={6} alignItems="center">
          {/* Form Section */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: '#fff', borderRadius: '16px' }}>
              <Typography variant="h4" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, mb: 4, color: '#1a1a1a' }}>
                Send Us Your Requirements
              </Typography>

              <Stack spacing={3}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Name"
                    variant="outlined"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    label="Company Name (Optional)"
                    variant="outlined"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Email"
                    variant="outlined"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    label="Contact No."
                    variant="outlined"
                    value={contactNo}
                    onChange={(e) => setContactNo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>

                <TextField
                  fullWidth
                  label="Requirements"
                  variant="outlined"
                  multiline
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />

                <Button
                  variant="contained"
                  onClick={sendEmail}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    backgroundColor: '#7C001F',
                    color: 'white',
                    py: 1.5,
                    px: 4,
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 600,
                    alignSelf: 'flex-start',
                    boxShadow: '0 4px 12px rgba(124, 0, 31, 0.2)',
                    '&:hover': {
                      backgroundColor: '#5C001F',
                      boxShadow: '0 6px 16px rgba(124, 0, 31, 0.3)',
                    }
                  }}
                >
                  SUBMIT
                </Button>

                <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #eee' }}>
                  <Typography variant="body2" sx={{ fontFamily: 'Inter, sans-serif', color: '#666', mb: 2 }}>
                    Prefer to contact us directly?
                  </Typography>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <PhoneIcon sx={{ color: '#7C001F', fontSize: 20 }} />
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, color: '#333' }}>
                        +91 9825394564
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <EmailIcon sx={{ color: '#7C001F', fontSize: 20 }} />
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, color: '#333' }}>
                        maxweighsystem@gmail.com
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Grid>

          {/* Image Section */}
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={EnquiryImage}
              alt="Weighing Scale Enquiry"
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: '20px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                objectFit: 'cover',
                maxHeight: '600px'
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Loading Modal */}
      <Modal open={loading} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff', padding: 4, borderRadius: 2, boxShadow: 24 }}>
          <CircularProgress sx={{ color: '#7C001F' }} />
          <Typography sx={{ marginLeft: 2, fontFamily: 'Inter, sans-serif' }}>Sending...</Typography>
        </Box>
      </Modal>

      {/* Alert Snackbar */}
      <Modal open={alertOpen} onClose={handleAlertClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert
          onClose={handleAlertClose}
          severity={alertSeverity}
          sx={{
            backgroundColor: alertSeverity === 'success' ? '#2e7d32' : '#d32f2f',
            color: '#fff',
            '& .MuiAlert-icon': { color: '#fff' }
          }}
        >
          {alertMessage}
        </Alert>
      </Modal>
    </Box>
  );
};

export default Enquiry;
